import React, { useContext } from 'react';
import throttle from 'lodash/throttle';
import shallowEqual from 'shallowequal';
import { breakpoints } from '@prisjakt/ui-base';

type ViewportContextValue = {
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktop?: boolean;
};

const ViewportContext = React.createContext<ViewportContextValue>({});
ViewportContext.displayName = 'ViewportContext';

let queries: {
  smallOnly: MediaQueryList;
  mediumOnly: MediaQueryList;
  largeUp: MediaQueryList;
};

const getSizes = () => {
  if (!window) {
    return { isMobile: true, isTablet: false, isDesktop: false };
  }

  if (!queries) {
    queries = {
      smallOnly: window.matchMedia(breakpoints.smallOnly),
      mediumOnly: window.matchMedia(breakpoints.mediumOnly),
      largeUp: window.matchMedia(breakpoints.largeUp),
    };
  }

  return {
    isMobile: queries.smallOnly.matches,
    isTablet: queries.mediumOnly.matches,
    isDesktop: queries.largeUp.matches,
  };
};

export const ViewportContextContainer = ({
  children,
  constValue = undefined,
}: {
  children: React.ReactNode;
  constValue?: ViewportContextValue;
}) => {
  // Always initially guess that we're using mobile, since thats
  // what the server guesses and what the initial hydration will assume is true on the client.
  // Then set the actual ViewPort in the effect.
  const [state, setState] = React.useState(
    constValue || {
      isMobile: true,
      isTablet: false,
      isDesktop: false,
    }
  );
  React.useEffect(() => {
    // This only runs in client-side, we set the actual value here. This prompts a rerender which is neccesary since
    // we the components will be hydrated assuming they isMobile is true.
    setState(getSizes());
    const onResize = throttle(() => {
      if (!constValue) {
        const sizes = getSizes();
        setState(s => {
          if (!shallowEqual(sizes, s)) {
            return sizes;
          }
          return s;
        });
      }
    }, 200);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [constValue]);
  return (
    <ViewportContext.Provider value={state}>
      {children}
    </ViewportContext.Provider>
  );
};

// Needed to return a ViewportContext value for enzyme tests.
// It's a workaround for shallow rendering with React.useContext hook.
export const useViewportContext = () => useContext(ViewportContext);

export default ViewportContext;
